import { type MaybeRef, unref } from 'vue';
import { debounce } from 'lodash-es';
import { defineStore } from 'pinia';
import { systemLib } from 'shared';

type ScrollPosition = {
  top: number;
  left: number;
};

export const useSavedScrollPosition = defineStore(
  'saved-page-scroll-position',
  () => {
    const isIos = systemLib.getMobileOperatingSystem() === 'ios';
    const scrollPositions: Record<string, ScrollPosition> = {};

    const set = (
      pageName: MaybeRef<string>,
      customPosition?: ScrollPosition,
    ) => {
      const key = unref(pageName);

      if (customPosition) {
        scrollPositions[key] = customPosition;
      } else if (isIos) {
        /**
         * For Ios body.scrollTop works fine, but window.scrollY always return 0.
         * Vice-versa for Android.
         * */
        scrollPositions[key] = {
          left: document.body.scrollLeft,
          top: document.body.scrollTop,
        };
      } else {
        scrollPositions[key] = { left: window.scrollX, top: window.scrollY };
      }
    };

    const get = (pageName: string) => {
      return scrollPositions[pageName];
    };

    const scroll = (
      pageName: MaybeRef<string>,
      behavior: ScrollBehavior = 'auto',
    ) => {
      const savedPosition = get(unref(pageName));
      if (savedPosition) {
        /**
         * For some reason, lockOverscrollBehaviour doesn't break "scroll" method for body tag,
         * so we can use it instead of scrollIntoView
         * */
        if (isIos) {
          document.body.scroll({ ...savedPosition, behavior });
        } else {
          window.scroll({ ...savedPosition, behavior });
        }
      }
    };

    const scrollToTop = (behavior: ScrollBehavior = 'auto') => {
      /**
       * Mirror the iOS/Android split used by `scroll`: on iOS the body is the
       * scroll container (window.scrollY stays 0), on Android it is the window.
       * Plain `window.scrollTo` is a no-op inside the iOS webview.
       */
      if (isIos) {
        document.body.scroll({ top: 0, left: 0, behavior });
      } else {
        window.scroll({ top: 0, left: 0, behavior });
      }
    };

    const scrollListener = (
      pageName: MaybeRef<string>,
      options = { delay: 100 },
    ) => {
      const listener = debounce(() => {
        set(unref(pageName));
      }, options.delay);

      const attach = () => {
        const target = isIos ? document.body : window;
        target.addEventListener('scroll', listener);
      };

      const detach = () => {
        const target = isIos ? document.body : window;
        target.removeEventListener('scroll', listener);
      };

      return {
        attach,
        detach,
      };
    };

    return {
      set,
      get,
      scroll,
      scrollToTop,
      scrollListener,
    };
  },
);
